import { siteConfig } from '../config/siteConfig';
import { PageLink } from "./PageLink";
import { PageHero } from "./PageHero";
import { useState } from 'react';
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Calendar, MessageCircle, BarChart3, Mail, CheckCircle, Loader2, AlertCircle } from "lucide-react";
import { Mark } from "./Mark";
import { submitDemoRequest, ApiError } from "../config/api";
import { trackEvent } from "../config/analytics";

const PHONE_HINT = 'Enter a phone number with country code, for example +91 80 4663 2400.';

interface ScheduleDemoPageProps {
  onNavigate: (page: string) => void;
  onNavigateToDemo: () => void;
}

export function ScheduleDemoPage({ onNavigate }: ScheduleDemoPageProps) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    jobTitle: '',
    country: '',
    demoDate: '',
    additionalInfo: ''
  });
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const validateField = (field: string, value: string): string | undefined => {
    const trimmed = value ? value.trim() : '';
    switch (field) {
      case 'firstName':
        if (!trimmed) return 'Enter your first name.';
        return undefined;
      case 'lastName':
        if (!trimmed) return 'Enter your last name.';
        return undefined;
      case 'company':
        if (!trimmed) return 'Enter your company name.';
        return undefined;
      case 'email':
        if (!trimmed) return 'Enter your work email.';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return 'Enter an email address like name@company.com.';
        return undefined;
      case 'phone':
        if (!trimmed) return undefined;
        // Supports +, -, space, and digits
        if (!/^\+?[\d\s\-]+$/.test(trimmed)) {
          return PHONE_HINT;
        }
        const digits = trimmed.replace(/\D/g, '');
        if (digits.length < 6 || digits.length > 16) {
          return PHONE_HINT;
        }
        return undefined;
      case 'jobTitle':
        if (!trimmed) return 'Enter your job title.';
        return undefined;
      case 'country':
        if (!trimmed) return 'Enter your country and time zone.';
        return undefined;
      default:
        return undefined;
    }
  };

  const handleBlur = (field: string) => {
    const error = validateField(field, formData[field as keyof typeof formData] || '');
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      const error = validateField(field, value);
      setErrors(prev => ({ ...prev, [field]: error }));
    }
    if (apiError) setApiError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);

    // Validate all mandatory and provided fields client-side
    const fieldsToValidate = ['firstName', 'lastName', 'company', 'email', 'phone', 'jobTitle', 'country'];
    const newErrors: Record<string, string | undefined> = {};
    let hasError = false;

    fieldsToValidate.forEach((field) => {
      const err = validateField(field, formData[field as keyof typeof formData] || '');
      if (err) {
        newErrors[field] = err;
        hasError = true;
      }
    });

    setErrors(newErrors);

    if (hasError) {
      // Do not call API if mandatory fields are missing or invalid
      return;
    }

    setIsLoading(true);
    try {
      await submitDemoRequest(formData);
      trackEvent('generate_lead', { form: 'schedule_demo' });
      setIsSubmitted(true);
    } catch (error: any) {
      if (error?.errors && Array.isArray(error.errors) && error.errors.length > 0) {
        const fieldErrors: Record<string, string> = {};
        const fieldMapping: Record<string, string> = {
          timezone: 'country',
          preferredTime: 'demoDate',
          preferredDate: 'demoDate',
          message: 'additionalInfo',
        };
        error.errors.forEach((err: any) => {
          const path = err.path || err.param;
          if (path) {
            const fieldName = fieldMapping[path] || path;
            if (!fieldErrors[fieldName]) {
              fieldErrors[fieldName] = err.msg;
            }
          }
        });
        setErrors((prev: any) => ({ ...prev, ...fieldErrors }));
      }
      // Server errors carry a message worth showing; a network failure ("Failed to fetch") doesn't.
      setApiError(error instanceof ApiError
        ? error.message
        : `We couldn't send this. Check your connection and try again, or email ${siteConfig.contact.sales}.`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <PageHero
        title={<>See TurfAI on <Mark>your workflow</Mark></>}
        lede="Tell us a little about your goals, and we'll tailor a 30-minute walkthrough to your use case."
        actions={
          <Button size="lg" className="h-12 px-6 text-base" onClick={() => document.getElementById('demo-form')?.scrollIntoView({ behavior: 'smooth' })}>
            Go to the form
          </Button>
        }
      />


      {/* Why Book Section */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-[36px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">What you'll get in <Mark>30 minutes</Mark></h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="p-8 border-softblack-tint-20 transition-shadow">
              <CardContent className="p-0 text-center">
                <div className="w-16 h-16 bg-indigo-tint-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Calendar className="w-8 h-8 text-royal-indigo" />
                </div>
                <h3 className="text-[22px] lg:text-[24px] font-semibold text-dark-indigo mb-4">An interactive tour</h3>
                <p className="text-soft-black">
                  Real workflows, dashboards and accelerators, run live.
                </p>
              </CardContent>
            </Card>
            <Card className="p-8 border-softblack-tint-20 transition-shadow">
              <CardContent className="p-0 text-center">
                <div className="w-16 h-16 bg-indigo-tint-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <MessageCircle className="w-8 h-8 text-royal-indigo" />
                </div>
                <h3 className="text-[22px] lg:text-[24px] font-semibold text-dark-indigo mb-4">Answers from an architect</h3>
                <p className="text-soft-black">
                  Chat directly with an Ariviti solution architect.
                </p>
              </CardContent>
            </Card>
            <Card className="p-8 border-softblack-tint-20 transition-shadow">
              <CardContent className="p-0 text-center">
                <div className="w-16 h-16 bg-indigo-tint-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <BarChart3 className="w-8 h-8 text-royal-indigo" />
                </div>
                <h3 className="text-[22px] lg:text-[24px] font-semibold text-dark-indigo mb-4">An ROI snapshot</h3>
                <p className="text-soft-black">
                  A savings estimate for your team.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Demo Request Form */}
      <section id="demo-form" className="py-20 lg:py-24 band-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-[36px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">Request your <Mark>demo</Mark></h2>
              <p className="text-[22px] text-soft-black">
                We reply within one business day.
              </p>
            </div>

            {!isSubmitted ? (
              <Card className="p-8 bg-white border-softblack-tint-20">
                <CardContent className="p-0">
                  <form onSubmit={handleSubmit} noValidate className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First name</Label>
                        <Input 
                          id="firstName"
                          required
                          value={formData.firstName}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                          onBlur={() => handleBlur('firstName')}
                          aria-invalid={!!errors.firstName}
                        />
                        {errors.firstName && <div className="text-danger-text text-xs mt-1">{errors.firstName}</div>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last name</Label>
                        <Input 
                          id="lastName"
                          required
                          value={formData.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          onBlur={() => handleBlur('lastName')}
                          aria-invalid={!!errors.lastName}
                        />
                        {errors.lastName && <div className="text-danger-text text-xs mt-1">{errors.lastName}</div>}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="company">Company</Label>
                      <Input 
                        id="company"
                        required
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        onBlur={() => handleBlur('company')}
                        aria-invalid={!!errors.company}
                      />
                      {errors.company && <div className="text-danger-text text-xs mt-1">{errors.company}</div>}
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="email">Work email</Label>
                        <Input 
                          id="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          onBlur={() => handleBlur('email')}
                          aria-invalid={!!errors.email}
                        />
                        {errors.email && <div className="text-danger-text text-xs mt-1">{errors.email}</div>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone (optional)</Label>
                        <Input 
                          id="phone"
                          type="tel"
                          placeholder="Include the country code"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          onBlur={() => handleBlur('phone')}
                          aria-invalid={!!errors.phone}
                        />
                        {errors.phone && <div className="text-danger-text text-xs mt-1">{errors.phone}</div>}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="jobTitle">Job title</Label>
                      <Input 
                        id="jobTitle"
                        required
                        value={formData.jobTitle}
                        onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                        onBlur={() => handleBlur('jobTitle')}
                        aria-invalid={!!errors.jobTitle}
                      />
                      {errors.jobTitle && <div className="text-danger-text text-xs mt-1">{errors.jobTitle}</div>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="country">Country and time zone</Label>
                      <Input 
                        id="country"
                        required
                        placeholder="For example, India (IST) or United Kingdom (GMT)"
                        value={formData.country}
                        onChange={(e) => handleInputChange('country', e.target.value)}
                        onBlur={() => handleBlur('country')}
                        aria-invalid={!!errors.country}
                      />
                      {errors.country && <div className="text-danger-text text-xs mt-1">{errors.country}</div>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="demoDate">Preferred date and time (optional)</Label>
                      <Input 
                        id="demoDate"
                        placeholder="For example, Tuesday afternoon IST"
                        value={formData.demoDate}
                        onChange={(e) => handleInputChange('demoDate', e.target.value)}
                        onBlur={() => handleBlur('demoDate')}
                        aria-invalid={!!errors.demoDate}
                      />
                      {errors.demoDate && <div className="text-danger-text text-xs mt-1">{errors.demoDate}</div>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="additionalInfo">What should we focus on? (optional)</Label>
                      <Textarea
                        id="additionalInfo"
                        placeholder="Your use case, team size, and what you'd like to see."
                        value={formData.additionalInfo}
                        onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
                        onBlur={() => handleBlur('additionalInfo')}
                        aria-invalid={!!errors.additionalInfo}
                      />
                      {errors.additionalInfo && <div className="text-danger-text text-xs mt-1">{errors.additionalInfo}</div>}
                    </div>

                    {apiError && (
                      <div className="flex items-start gap-2 p-4 bg-orange-tint-20 border border-orange-tint-20 rounded-lg text-danger-text">
                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <p className="text-sm">{apiError}</p>
                      </div>
                    )}

                    <Button type="submit" size="lg" className="w-full h-11 text-[22px]" disabled={isLoading}>
                      {isLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Request demo'
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            ) : (
              <Card className="p-8 bg-white border-softblack-tint-20">
                <CardContent className="p-0 text-center">
                  <div className="w-16 h-16 bg-indigo-tint-20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-royal-indigo" />
                  </div>
                  <h3 className="text-[22px] lg:text-[24px] font-semibold text-dark-indigo mb-4">Request received</h3>
                  <p className="text-soft-black mb-6">
                    A solution architect will email you within one business day to confirm a time.
                  </p>
                  <Button variant="outline" asChild><PageLink page={'platform'} onNavigate={onNavigate}>
                    Back to the platform
                  </PageLink></Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Secondary CTA */}
      <section className="py-16 bg-dark-indigo text-white">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-[28px] font-semibold mb-4">Have a question first?</h3>
          <div className="flex justify-center items-center">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              <a href={`mailto:${siteConfig.contact.sales}`} className="hover:underline">
                {siteConfig.contact.sales}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}